import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Route, useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { nanoid } from 'nanoid';
import { ThemeProvider } from '@mui/material/styles';
import { Button } from '@upmc/ims.component-library';
import { FAB } from '@upmc/ims.component-library';
import MaterialIcon from '@material/react-material-icon';
import Base from '../Base/Base';
import Icon from '../../components/UI/MDC/Icon/Icon';
import UpdateAppTables from './actions/UpdateAppTables';
import UpdateEmailNotifications from './actions/UpdateEmailNotifications/UpdateEmailNotifications';
import UpdateApprovals from './actions/UpdateApprovals/UpdateApprovals';
import ExtensionManagement from './actions/ExtensionManagement/ExtensionManagement';
import ComponentManagement from './actions/ComponentManagement/ComponentManagement';
import AppDependencyManagement from './actions/AppDependencyManagement/AppDependencyManagement';
import ADGroupManagement from './actions/ADGroupManagement/ADGroupManagement';
import WQSystemManagement from './actions/WQSystemManagement/WQSystemManagement';
import ServiceAccountManagement from './actions/ServiceAccountManagement/ServiceAccountManagement';
import DistributionListConfiguration from './actions/DistributionListConfiguration/DistributionListConfiguration';
import ApplicationsSearch from '../../components/AppUtils/ApplicationsSearch';
import UnauthorizedMessage from './components/UnauthorizedMessage';
import ApplicationSettings from './components/ApplicationSettings';
import ReqNumModal from './components/ReqNumModal';
import { ImportExport } from './components/ImportExport/ImportExport';
import { theme } from '../../components/layouts/theme';
import { AUTHORIZEDROLES, ACTIONS, AD_TYPES, APP_DEPENDENCY_EXCLUDED_APP_IDS } from './_constants';
import { GetApplicationsInfoIndex } from '../ApplicationInformation/ApplicationInformation_api';
import styles from './ApplicationMaintenance.module.scss';

const ApplicationMaintenance = ({ currentUser }) => {
	const history = useHistory();
	const location = useLocation();
	const preSelectedAppID = new URLSearchParams(location.search).get('appID');

	/**
	 * @type {[import('Classes/ResponseModels/Apps').AppModel[], Function]}
	 */
	const [apps, setApps] = useState(null);
	/**
	 * @type {[import('Classes/ResponseModels/Apps').AppModel, Function]}
	 */
	const [selectedApp, setSelectedApp] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	/**
	 * @typedef {Object} ActionKey
	 * @property {string} key
	 * @property {string} value
	 */
	/**
	 * @type [ActionKey[], Function]
	 */
	const [sActionKeys, setActionKeys] = useState(
		Object.keys(ACTIONS).map(key => {
			return {
				key: nanoid(),
				value: ACTIONS[key],
			};
		})
	);
	const [requirementNumber, setRequirementNumber] = useState(null);
	const [reqNumModalOpen, setReqNumModalOpen] = useState(false);
	const [readOnly, setReadOnly] = useState(true);
	const [sArrErrors, setArrErrors] = useState([]);

	const isUserAuthorized = useMemo(
		() =>
			currentUser?.Roles?.some(role =>
				AUTHORIZEDROLES.includes(role.Full)
			),
		[currentUser]
	);

	useEffect(() => {
		setIsLoading(true);

		GetApplicationsInfoIndex().then(response => {
			let apps = response;

			setApps(apps?.applications);

			setIsLoading(false);
		});
	}, []);

	useEffect(() => {
		if (preSelectedAppID && preSelectedAppID !== 0) {
			let preselectedApp = apps?.find(
				app => app.id === parseInt(preSelectedAppID)
			);

			if (preselectedApp && preselectedApp.id !== selectedApp?.id) {
				setSelectedApp(preselectedApp);
			}
		} else {
			//reset route to force user to select specific action after changing the selected app IF AppID param not present
			history.push('/ApplicationMaintenance');
		}
	}, [history, apps, selectedApp, preSelectedAppID]);

	/**
	 * Memoized function to retrieving an action's key from local state.
	 */
	const getKey = useCallback(
		action => sActionKeys.find(objKey => objKey.value === action)?.key,
		[sActionKeys]
	);

	/**
	 * Memoized function to force an action's component to reset state and re-create.
	 * Useful for triggering action reloads.
	 */
	const setKey = useCallback(action => {
		setActionKeys(arrKeys => {
			let arrKeysCopy = [...arrKeys];
			let indexKey = arrKeysCopy.findIndex(el => el.value === action);
			if (indexKey) {
				arrKeysCopy[indexKey].key = nanoid();
			}

			return arrKeysCopy;
		});
	}, []);

	const arrActionRoutes = useMemo(() => {
		return [
			{
				label: ACTIONS.BASE,
				route: '/ApplicationMaintenance/UpdateAppTables',
				component: (
					<UpdateAppTables
						key={getKey(ACTIONS.BASE)}
						appID={selectedApp?.id}
						currentUser={currentUser}
						requirementNumber={requirementNumber}
						readOnly={readOnly}
					/>
				),
			},
			{
				label: ACTIONS.NOTIFICATIONS,
				route: '/ApplicationMaintenance/UpdateEmailNotifications',
				component: (
					<UpdateEmailNotifications
						key={getKey(ACTIONS.NOTIFICATIONS)}
						appID={selectedApp?.id}
						currentUser={currentUser}
						requirementNumber={requirementNumber}
						readOnly={readOnly}
					/>
				),
			},
			{
				label: ACTIONS.APPROVALS,
				route: '/ApplicationMaintenance/UpdateApprovals',
				component: (
					<UpdateApprovals
						key={getKey(ACTIONS.APPROVALS)}
						appID={selectedApp?.id}
						currentUser={currentUser}
						requirementNumber={requirementNumber}
						readOnly={readOnly}
					/>
				),
			},
			{
				label: ACTIONS.EXTENSIONS,
				route: '/ApplicationMaintenance/ManageExtensions',
				component: (
					<ExtensionManagement
						key={getKey(ACTIONS.EXTENSIONS)}
						appID={selectedApp?.id}
						currentUser={currentUser}
						requirementNumber={requirementNumber}
						readOnly={readOnly}
					/>
				),
			},
			{
				label: ACTIONS.COMPONENTS,
				route: '/ApplicationMaintenance/UpdateComponents',
				component: (
					<ComponentManagement
						key={getKey(ACTIONS.COMPONENTS)}
						appID={selectedApp?.id}
						currentUser={currentUser}
						requirementNumber={requirementNumber}
						readOnly={readOnly}
					/>
				),
			},
			{
				label: ACTIONS.APP_DEPENDENCY,
				route: '/ApplicationMaintenance/AppDependencyManagement',
				component: (
					<AppDependencyManagement
						key={getKey(ACTIONS.APP_DEPENDENCY)}
						appID={selectedApp?.id}
						currentUser={currentUser}
						requirementNumber={requirementNumber}
						readOnly={readOnly}
					/>
				),
			},
			{
				label: ACTIONS.AD_GROUPS,
				route: '/ApplicationMaintenance/ADGroupManagement',
				component: (
					<ADGroupManagement
						key={getKey(ACTIONS.AD_GROUPS)}
						appID={selectedApp?.id}
						type={AD_TYPES.ON_PREM}
						currentUser={currentUser}
						errors={sArrErrors}
						requirementNumber={requirementNumber}
						readOnly={readOnly}
						setErrors={setArrErrors}
						setKey={() => setKey(ACTIONS.AD_GROUPS)}
					/>
				),
			},
			{
				label: ACTIONS.CLOUD_GROUPS,
				route: '/ApplicationMaintenance/CloudGroupManagement',
				component: (
					<ADGroupManagement
						key={getKey(ACTIONS.CLOUD_GROUPS)}
						appID={selectedApp?.id}
						type={AD_TYPES.CLOUD}
						currentUser={currentUser}
						errors={sArrErrors}
						requirementNumber={requirementNumber}
						readOnly={readOnly}
						setErrors={setArrErrors}
						setKey={() => setKey(ACTIONS.CLOUD_GROUPS)}
					/>
				),
			},
			{
				label: ACTIONS.EU_GROUPS,
				route: '/ApplicationMaintenance/EUGroupManagement',
				component: (
					<ADGroupManagement
						key={getKey(ACTIONS.EU_GROUPS)}
						appID={selectedApp?.id}
						type={AD_TYPES.EU}
						currentUser={currentUser}
						errors={sArrErrors}
						requirementNumber={requirementNumber}
						readOnly={readOnly}
						setErrors={setArrErrors}
						setKey={() => setKey(ACTIONS.EU_GROUPS)}
					/>
				),
			},
			{
				label: ACTIONS.DL,
				route: '/ApplicationMaintenance/DLConfiguration',
				component: (
					<DistributionListConfiguration
						key={getKey(ACTIONS.DL)}
						appID={selectedApp?.id}
						currentUser={currentUser}
						requirementNumber={requirementNumber}
						readOnly={readOnly}
					/>
				),
			},
			{
				label: ACTIONS.WORK_QUEUE,
				route: '/ApplicationMaintenance/WQSystemManagement',
				component: (
					<WQSystemManagement
						key={getKey(ACTIONS.WORK_QUEUE)}
						appID={selectedApp?.id}
						currentUser={currentUser}
						requirementNumber={requirementNumber}
						readOnly={readOnly}
					/>
				),
			},
			{
				label: ACTIONS.SERVICE_ACCOUNTS,
				route: '/ApplicationMaintenance/ServiceAccountManagement',
				component: (
					<ServiceAccountManagement
						key={getKey(ACTIONS.SERVICE_ACCOUNTS)}
						appID={selectedApp?.id}
						currentUser={currentUser}
						requirementNumber={requirementNumber}
						readOnly={readOnly}
					/>
				),
			},
		];
	}, [
		currentUser,
		requirementNumber,
		readOnly,
		selectedApp,
		getKey,
		setKey,
		sArrErrors,
		setArrErrors
	]);

	const onRequirementChangeHandler = useCallback(input => {
		setRequirementNumber(input);
		setReadOnly(input === undefined || input === null);
	}, []);

	const handleAppChange = e => {
		const appID = e.target.value;
		const app = apps?.find(x => x.id === appID);

		//display warning message if app.appAux.isSafeToEdit is not true
		if (!app.appAux.isSafeToEdit) {
			if (
				window.confirm(
					'This application has been identified as potentially unsafe to modify via this tool due to data dependencies. Attempting to modify this application may result in unexpected issues with application request processing. Would you like to continue?'
				)
			) {
				setSelectedApp(app);
				onRequirementChangeHandler(null);
				setReqNumModalOpen(true);
			}
		} else {
			setSelectedApp(app);
			onRequirementChangeHandler(null);
			setReqNumModalOpen(true);
		}
	};

	return (
		<ThemeProvider theme={theme}>
			<Base
				title='Application Management'
				loading={isLoading}
				className={styles.APPLY_FONT}
			>
				<ReqNumModal
					open={reqNumModalOpen}
					setOpen={setReqNumModalOpen}
					setReqNum={onRequirementChangeHandler}
					reqNum={requirementNumber}
					omitReadOnly={false}
				/>
				{isUserAuthorized !== null ? (
					isUserAuthorized && currentUser ? (
						apps ? (
							<>
								<Link
									to='/ApplicationMaintenance/NewApplication'
									//inline style applied due to failure to implement via scss
									style={{ textDecoration: 'none' }}
									id='link_AppNew'
								>
									<FAB
										exited={false}
										icon={<Icon icon='add' />}
										text='New App'
										sx={{ position: 'relative' }}
									/>
								</Link>
								<div className={styles.selectionControls}>
									<div className={styles.flex_Vert}>
										<ApplicationsSearch
											options={apps}
											onChange={handleAppChange}
											placeholder={
												'...Search By App Name'
											}
										/>
									</div>
								</div>

								<div className={styles.SelectedAppHeader}>
									{selectedApp ? (
										<h3>
											{
												selectedApp?.appInfo
													.applicationName
											}{' '}
											({selectedApp?.id})
										</h3>
									) : null}
									<ApplicationSettings
										selectedApp={selectedApp}
										currentUser={currentUser}
										setSelectedApp={setSelectedApp}
									/>
									<ImportExport
										appID={selectedApp?.id}
										selectApp={setSelectedApp}
										requirementID={requirementNumber}
										user={currentUser}
									/>
								</div>

								{selectedApp ? (
									<>
										<div
											className={
												styles.ActionButtonContainer
											}
										>
											<Link
												to={`/ApplicationInfo?UPMCAppId=${selectedApp?.appInfo?.upmc_App_ID}`}
												target='_top'
												className={styles.link}
											>
												<Button
													icon={
														<MaterialIcon icon='edit' />
													}
													variant='outlined'
												>
													App Info
												</Button>
											</Link>
											{/*render links*/}
											{arrActionRoutes?.filter(
												action =>
													action.label !== ACTIONS.APP_DEPENDENCY ||
													!APP_DEPENDENCY_EXCLUDED_APP_IDS.includes(selectedApp?.id)
											).map(
												(action, index) => {
													return (
														<Link
															to={action.route}
															className={
																styles.link
															}
															key={
																'route' +
																index.toString()
															}
														>
															<Button
																variant='outlined'
																icon={
																	<MaterialIcon icon='edit' />
																}
															>
																{action.label}
															</Button>
														</Link>
													);
												}
											)}
										</div>
										<strong>
											<hr />
										</strong>
										{selectedApp ? (
											<div
												className={classNames([
													styles.text_center,
													styles.row,
												])}
											>
												{readOnly ? (
													<div>
														<strong>
															Read Only
														</strong>
													</div>
												) : (
													<div>
														<strong>
															Requirement Number:{' '}
															{requirementNumber}
														</strong>
													</div>
												)}
												<Button
													startIcon={
														<MaterialIcon icon='edit' />
													}
													onClick={() =>
														setReqNumModalOpen(true)
													}
													sx={{ m: '1rem' }}
												>
													Set Requirement Number
												</Button>
											</div>
										) : null}
										{/*render routes to components*/}
										{arrActionRoutes?.map(
											(action, index) => {
												return (
													<Route
														path={action.route}
														render={() =>
															action.component
														}
														key={
															'link' +
															index.toString()
														}
													/>
												);
											}
										)}
									</>
								) : null}
							</>
						) : null
					) : (
						<UnauthorizedMessage currentUser={currentUser} />
					)
				) : null}
			</Base>
		</ThemeProvider>
	);
};

export default ApplicationMaintenance;
